import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { DonorStd } from '../../models/DonorStd';
import { DonorStdFile } from '../../models/DonorStdFile';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';

const MypageCheckupStd = () => {
  const { get, post, loading } = useApi();
  const navigate = useNavigate();

  const [std, setStd] = useState<DonorStd | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [delFileIds, setDelFileIds] = useState<number[]>([]);

  useTitle('Ton Bébé | 性病検査結果');

  const fetchStdData = useCallback(() => {
    get({
      endpoint: `/api/v1/donor_stds/me`,
      onSuccess: (res) => {
        setNewFiles([]);
        setDelFileIds([]);

        const persistedStd = new DonorStd(res.data);
        if (persistedStd.expired) {
          setStd(new DonorStd());
        } else {
          setStd(persistedStd);
        }
      },
      onError: (err) => {
        toast.error("情報の取得に失敗しました。時間をおいてお試しください。");
      },
    });
  }, [ get ]);

  useEffect(() => {
    fetchStdData();
  }, [ fetchStdData ]);

  const handleSave = () => {
    if (std?.status === 'SUBMITTED') {
      toast.error("申請中のため、編集できません。");
      return false;
    }

    if (std?.status === 'CERTIFIED' && !std?.expired) {
      if (!window.confirm('保存すると再申請＆承認されるまで未提出扱いとなります。保存しますか？')){
        return;
      }
    }

    if (std?.collectedOn === '') {
      toast.error("採取日を入力してください。");
      return false;
    }

    if (!std?.donorStdFiles || (std.donorStdFiles.length === 0 && newFiles.length === 0)) {
      toast.error("検査結果書類を1つ以上添付してください。");
      return false;
    }
    if ((std?.donorStdFiles?.length || 0) + newFiles.length > 5) {
      toast.error("最大5ファイルまで添付できます。");
      return false;
    }

    if (std?.total !== 'MINUS') {
      toast.error("陽性の項目があります。");
      return false;
    }

    if (!std?.in6Month) {
      toast.error("6ヶ月以内の検査結果のみ登録可能です。");
      return false;
    }

    const formData = new FormData();
    formData.append('collected_on', std?.collectedOn);
    formData.append('hiv', std?.hiv);
    formData.append('syphilis', std?.syphilis);
    formData.append('chlamydia', std?.chlamydia);
    formData.append('gonorrhea', std?.gonorrhea);
    formData.append('trichomoniasis', std?.trichomoniasis);
    formData.append('candidiasis', std?.candidiasis);
    formData.append('hepatitis_b', std?.hepatitisB);
    formData.append('hepatitis_c', std?.hepatitisC);
    formData.append('del_file_ids', delFileIds.join(','));
    for (const file of newFiles) {
      formData.append('files[]', file);
    }

    post({
      endpoint: '/api/v1/donor_stds/me',
      method: 'PATCH',
      formData: formData,
      onSuccess: () => {
        toast.success("保存しました。");
        navigate('/mypage/checkups');
      },
      onError: (err) => {
        toast.error("保存に失敗しました。時間をおいて再度お試しください。");
      },
    })
  }

  return (
    <main className="sub">
      { loading && <Spinner overlay={ true } /> }
      <section className="donor_std">
        <h2 className="main_title"><span>性病検査結果</span></h2>
        <div className="form">
          {std?.status === 'REMANDED' && (
            <div className="border_box pink">
              <span className="txt_pink alert_msg">
                {std.comments && std.comments.split('\n').map((line, idx) => (
                  <React.Fragment key={ idx }>
                    { line }<br />
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          <dl className="graf_list">
            <dt>採取日 *</dt>
            <dd>
              <input
                type="date"
                value={ std?.collectedOn || '' }
                onChange={ e => setStd(prev => prev ? prev.set("collectedOn", e.target.value) : null) }
              />
            </dd>
            <dt>HIV *</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.hiv || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("hiv", e.target.value) : null) }
                >
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>梅毒 *</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.syphilis || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("syphilis", e.target.value) : null) }
                >
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>クラミジア *</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.chlamydia || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("chlamydia", e.target.value) : null) }
                >
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>淋菌 *</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.gonorrhea || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("gonorrhea", e.target.value) : null) }
                >
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>トリコモナス</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.trichomoniasis || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("trichomoniasis", e.target.value) : null) }
                >
                  <option value="NA">検査対象外</option>
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>カンジダ</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.candidiasis || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("candidiasis", e.target.value) : null) }
                >
                  <option value="NA">検査対象外</option>
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>B型肝炎</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.hepatitisB || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("hepatitisB", e.target.value) : null) }
                >
                  <option value="NA">検査対象外</option>
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
            <dt>C型肝炎</dt>
            <dd>
              <div className="select select01">
                <select
                  value={ std?.hepatitisC || '' }
                  onChange={ e => setStd(prev => prev ? prev.set("hepatitisC", e.target.value) : null) }
                >
                  <option value="NA">検査対象外</option>
                  <option value="MINUS">陰性</option>
                  <option value="PLUS">陽性</option>
                </select>
              </div>
            </dd>
          </dl>
          <div className="judge">
            総合判定
            <span className={ std?.total === 'MINUS' ? 'org' : 'txt_pink' } >
              { std?.totalText }
            </span>
          </div>
          <strong>※１つでも「陽性」がある場合、登録申請できません。</strong>
        </div>
        <h3 className="sub_title">検査結果書類をアップロードしてください</h3>
        <div className="filebtn">
          {(() => {
            // 保存済みファイル（DonorStdFile）と新規ファイル（File）を結合し最大5個まで表示
            const filesToDisplay = [
              ...std?.donorStdFiles || [],
              ...newFiles
            ].slice(0, 5);
            const totalCount = filesToDisplay.length;
            return (
              <>
                {filesToDisplay.map((file, idx) => (
                  <div key={idx} className="files_wrap">
                    <span className="filename">
                      {file ? file.name : "ファイルが選択されていません"}
                    </span>
                    {('id' in file) ? (
                      // 保存済みファイル
                      <label>
                        <button
                          type="button"
                          className="fileinput"
                          onClick={() => {
                            // delFileIdsに追加し、donorStdFilesから除外
                            setDelFileIds(ids => [...ids, (file as DonorStdFile).id]);
                            setStd(prev => prev ? prev.set(
                              "donorStdFiles",
                              prev.donorStdFiles.filter(f => (f as DonorStdFile).id !== (file as DonorStdFile).id)
                            ) : null);
                          }}
                        />削除
                      </label>
                    ) : (
                      // 新規ファイル
                      <label>
                        <button
                          type="button"
                          className="fileinput"
                          onClick={() => {
                            setNewFiles(prev => prev.filter(f => f.name !== (file as File).name));
                          }}
                        />削除
                      </label>
                    )}
                  </div>
                ))}
                {/* 空きスロットにファイル選択を表示 */}
                {[...Array(5 - totalCount)].map((_, idx) => (
                  <div key={totalCount + idx} className="files_wrap">
                    <span className="filename">ファイルが選択されていません</span>
                    <label>
                      <input
                        type="file"
                        name={`ファイル選択${totalCount + idx}`}
                        className="fileinput"
                        accept="image/*,application/pdf"
                        onChange={e => {
                          const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                          if (!file) return;
                          setNewFiles(prev => [...prev, file].slice(0, 5));
                        }}
                      />ファイル選択
                    </label>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
        <ul className="box">
          <li><h2>【注意事項】</h2></li>
          <li>＊検査結果書類は<strong>「検査機関」</strong>と<strong>「検査ID（報告書番号、キットIDなど）」</strong>がわかるものを提出してください。</li>
          <li>＊病院での検査結果の場合、<strong>「診察券」「診療明細」</strong>のコピーも提出してください。</li>
          <li>＊提出書類をもとに検査機関に問い合わせを行う場合があります。</li>
          <li>＊書類の信憑性を確認できない場合は登録をお断りします。書類の偽造など悪質な場合は、返金致しませんのでご了承ください。</li>
          <li>＊書類の<strong className="text-red">偽造/変造は犯罪</strong>です。刑法により処罰される可能性があります。</li>
          <li><h2>【入力項目と書類について】</h2></li>
          <li>＊１つでも「陽性」がある場合、登録申請できません。</li>
          <li>＊検査結果書類はウェブ画面のスクリーンショットでも可能です。</li>
          <li>＊検査結果書類の画像を他ユーザーが閲覧することはありません。</li>
          <li>＊採取日の記載が書類にない場合は、検査日/受付日などの日付でも登録可能です。</li>
          <li>＊書類に記載のある項目は全て記入してください。記入漏れがある場合は、運営事務局にて書類を基に訂正を行います。</li>
          <li>＊検査結果の有効期間は採取日から１年としております。</li>
          <li>＊6ヶ月以内の検査結果のみ登録可能です。</li>
          <li>＊個人情報の取り扱いについては<Link to="/privacy">プライバシーポリシー</Link>のページをご覧ください。</li>
        </ul>
        <ul className="btnarea">
          <li>
            <input
              type="submit"
              value={ std?.status === 'REMANDED' ? "再提出する" : "保存する" }
              className="w_bluebtn"
              onClick={ handleSave }
              disabled={ loading }
            />
          </li>
          <li>
            <Link to="/mypage/checkups" className="w_bluebtn">
              戻る
            </Link>
          </li>
        </ul>
      </section>
    </main>
  );
}

export default MypageCheckupStd;
